import { liveDistance, liveHectares, liveSquareMetres, parseTargetHa, targetReading, type LiveEffective, type LiveShape } from '../domain/liveMeasure';

const esc = (value: unknown): string => String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);

/**
 * V1.9.3 H · the small floating card of the live measure. A plain HTML element placed over the map: it is not a map
 * feature, so it is never saved, never exported (PNG is drawn from the map canvas) and needs no modal. It exists
 * only between `open()` and `close()`. The optional «Superficie objetivo» is a temporary reference kept for the
 * session in this card: it is not part of the project and never changes a geometry or a measure.
 */
export class LiveMeasureCard {
  private readonly element: HTMLElement;
  private target: number | null = null;
  private changed: () => void = () => undefined;
  private open_ = false;

  constructor(host: HTMLElement) {
    this.element = document.createElement('div');
    this.element.className = 'live-measure'; this.element.hidden = true;
    this.element.setAttribute('role', 'status'); this.element.setAttribute('aria-live', 'off'); this.element.dataset.liveMeasure = '';
    this.element.innerHTML = `<div class="lm-head" data-lm-title></div>
      <div class="lm-body" data-lm-body></div>
      <label class="lm-target"><span>Objetivo (ha)</span><input data-lm-target inputmode="decimal" autocomplete="off" placeholder="opcional" aria-label="Superficie objetivo en hectáreas (opcional)" /></label>
      <div class="lm-diff" data-lm-diff hidden></div>`;
    host.append(this.element);
    const input = this.input;
    input.addEventListener('input', () => { this.target = parseTargetHa(input.value); this.changed(); });
    // Only the objective field takes clicks and touches; everywhere else the card lets them through to the map, so a
    // vertex can still be placed under it (it then steps aside, see dodge()).
    ['pointerdown', 'mousedown', 'touchstart', 'click', 'dblclick'].forEach((type) => input.closest('label')!.addEventListener(type, (event) => event.stopPropagation()));
    // Esc inside the objective field only leaves the field; the global Esc (cancel the drawing) still works elsewhere.
    input.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === 'Escape') { event.stopPropagation(); input.blur(); } });
  }

  private get input(): HTMLInputElement { return this.element.querySelector<HTMLInputElement>('[data-lm-target]')!; }

  get visible(): boolean { return this.open_ && !this.element.hidden; }
  get targetHa(): number | null { return this.target; }
  onTargetChange(listener: () => void): void { this.changed = listener; }

  open(title: string): void {
    this.open_ = true; this.element.hidden = false; this.element.classList.remove('at-right');
    this.element.querySelector('[data-lm-title]')!.textContent = title;
    this.element.dataset.state = 'none';
  }

  close(): void {
    this.open_ = false; this.element.hidden = true;
    this.element.querySelector('[data-lm-body]')!.innerHTML = '';
    if (document.activeElement === this.input) this.input.blur();
  }

  /** Moves the card to the other side while the pointer (or a tap) is over, or about to reach, it. */
  dodge(event: MouseEvent | PointerEvent): void {
    if (!this.open_) return;
    const rect = this.element.getBoundingClientRect(); const margin = 28;
    if (event.clientX >= rect.left - margin && event.clientX <= rect.right + margin && event.clientY >= rect.top - margin && event.clientY <= rect.bottom + margin) this.element.classList.toggle('at-right');
  }

  show(shape: LiveShape | null, effective: LiveEffective | null, split: boolean): void {
    if (!this.open_) return;
    const body = this.element.querySelector<HTMLElement>('[data-lm-body]')!; const diff = this.element.querySelector<HTMLElement>('[data-lm-diff]')!;
    const targetRow = this.element.querySelector<HTMLElement>('.lm-target')!;
    if (!shape) { body.innerHTML = '<span class="lm-hint">Marca el primer punto y mueve el cursor…</span>'; targetRow.hidden = true; diff.hidden = true; this.element.dataset.state = 'none'; return; }
    if (shape.kind === 'line') {
      targetRow.hidden = true; diff.hidden = true; this.element.dataset.state = 'none';
      body.innerHTML = `<div class="lm-primary"><b>${esc(liveDistance(shape.totalM))}</b><small>longitud</small></div><div class="lm-secondary"><span>Tramo en curso <b>${esc(liveDistance(shape.segmentM))}</b></span><span>${shape.vertices - 1} vértice${shape.vertices - 1 === 1 ? '' : 's'}</span></div>`;
      return;
    }
    targetRow.hidden = false;
    // The headline is the surface that will count: the effective one inside a parent, the drawn one otherwise.
    const headlineM2 = effective ? effective.effectiveM2 : shape.areaM2;
    const rows: string[] = [];
    if (effective) {
      rows.push(`<span>Dibujada <b>${esc(liveHectares(effective.grossM2))}</b></span>`);
      rows.push(`<span class="${effective.outsideM2 > 1 ? 'lm-warn' : ''}">Fuera del límite <b>${esc(liveHectares(effective.outsideM2))}</b></span>`);
      if (split) rows.push(`<span>Resto de la Suerte <b>${esc(liveHectares(effective.restM2))}</b></span>`);
    } else rows.push(`<span>Perímetro <b>${esc(liveDistance(shape.perimeterM))}</b></span>`);
    body.innerHTML = `<div class="lm-primary"><b>${esc(liveHectares(headlineM2))}</b><small>${esc(liveSquareMetres(headlineM2))}${effective ? ' efectivos' : ''}</small></div><div class="lm-secondary">${rows.join('')}</div>`;
    const reading = targetReading(headlineM2, this.target);
    this.element.dataset.state = reading.state;
    diff.hidden = reading.state === 'none';
    diff.innerHTML = reading.state === 'none' ? '' : `<span class="lm-dot" aria-hidden="true"></span><span>${esc(reading.text)}</span>`;
  }
}
